import React, { useEffect, useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { LockIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
const privacyPoints = [{
  title: 'No storage. No selling.',
  description: 'Emigo has no access to your chat history or what others send you. It only processes what you type or upload — and never stores it.'
}, {
  title: 'Anonymous by design.',
  description: 'Every message flows through the same secure channel, with no way to tell who sent what.'
}, {
  title: 'Opt-out by default.',
  description: 'You are opted out of all data sharing from day one. Any future features that collect data will always be opt-in.'
}, {
  title: 'Nothing left behind.',
  description: 'Uninstalling clears all local data immediately. Your words stay yours.'
}];
export function PrivacySection() {
  const navigate = useNavigate();
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(entries => entries[0].isIntersecting && setVisible(true), {
      threshold: 0.1
    });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return <section ref={ref} className="relative py-24 md:py-32" style={{
    backgroundColor: 'var(--cream)'
  }} data-id="element-182">
      <div className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-10" data-id="element-183">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={visible ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.8,
        ease: [0.16, 1, 0.3, 1]
      }} className="max-w-3xl mb-14 md:mb-16" data-id="element-184">
          <p className="text-xs tracking-widest uppercase mb-4" style={{
          color: 'var(--coral)'
        }} data-id="element-185">
            Privacy first
          </p>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-normal leading-[1.1] tracking-tight mb-5" style={{
          color: 'var(--navy)',
          fontFamily: "'DM Serif Display', serif"
        }} data-id="element-186">
            Your words{' '}
            <span className="italic" style={{
            color: 'var(--coral)'
          }} data-id="element-187">
              stay yours.
            </span>
          </h2>
          <button onClick={() => navigate('/privacy-policy')} className="inline-flex items-center gap-1.5 text-sm font-medium transition-colors" style={{
          color: 'var(--coral)'
        }} data-id="element-188">
            <LockIcon className="w-4 h-4" data-id="element-189" />
            <span className="underline underline-offset-4 decoration-1" data-id="element-190">
              See full privacy policy
            </span>
          </button>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-5 md:gap-6" data-id="element-191">
          {privacyPoints.map((point, i) => <motion.div key={point.title} initial={{
          opacity: 0,
          y: 24
        }} animate={visible ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.7,
          delay: 0.1 + i * 0.08,
          ease: [0.16, 1, 0.3, 1]
        }} className="p-8 rounded-2xl" style={{
          backgroundColor: '#FFFFFF',
          border: '1px solid var(--border-soft)'
        }} data-id="element-192">
              <h3 className="text-xl md:text-2xl font-normal mb-2" style={{
            color: 'var(--navy)',
            fontFamily: "'DM Serif Display', serif"
          }} data-id="element-193">
                {point.title}
              </h3>
              <p className="text-base leading-relaxed" style={{
            color: 'var(--muted)'
          }} data-id="element-194">
                {point.description}
              </p>
            </motion.div>)}
        </div>
      </div>
    </section>;
}
export default PrivacySection;